import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiCheckCircle,
  HiXCircle,
  HiMinusCircle,
  HiChevronDown,
  HiChevronRight,
  HiBanknotes,
  HiChartBar,
  HiCog,
  HiUserGroup,
  HiShieldCheck,
  HiClipboardDocumentCheck,
  HiPlus,
  HiPencilSquare,
  HiTrash,
  HiPaperClip,
  HiArrowUpTray,
} from 'react-icons/hi2';
import { toast } from 'react-toastify';
import ConfirmationModal from '../SharedFeature/ConfirmationModal';
import {
  getEffectiveDiagnosticResponse,
  mapDiagnosticResponseToStatus,
} from './healthCheckHelpers';

function getCategoryDiagnosticScore(categoryKey, checklistData) {
  const items = checklistData[categoryKey] || [];
  if (items.length === 0) return 0;
  const answered = items.filter(i => {
    const r = getEffectiveDiagnosticResponse(i);
    return r === 'yes' || r === 'partial' || r === 'no';
  });
  if (answered.length === 0) return 0;
  let pts = 0;
  answered.forEach(i => {
    const r = getEffectiveDiagnosticResponse(i);
    if (r === 'yes') pts += 1;
    else if (r === 'partial') pts += 0.5;
  });
  return Math.round((pts / answered.length) * 100);
}

function getOverallDiagnosticScore(categoryDefs, checklistData) {
  const keys = categoryDefs.map(c => c.key);
  const nonEmpty = keys.filter(k => (checklistData[k] || []).length > 0);
  if (nonEmpty.length === 0) return 0;
  const sum = nonEmpty.reduce((s, k) => s + getCategoryDiagnosticScore(k, checklistData), 0);
  return Math.round(sum / nonEmpty.length);
}

const DiagnosticChecklist = ({
  checklistData,
  userRole,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onFileUpload,
  uploading,
}) => {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    categoryKey: null,
    itemId: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [newItem, setNewItem] = useState({ category: '', question: '', priority: 'medium' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [fileInputs, setFileInputs] = useState({});
  const [showNotes, setShowNotes] = useState(true);

  const canEdit = userRole === 'admin' || userRole === 'coach' || userRole === 'client';
  const canAdd = userRole === 'admin' || userRole === 'coach';
  const canDelete = userRole === 'admin';
  const canUpload = canEdit;

  const categories = useMemo(
    () => [
      {
        key: 'finance',
        title: 'Financial Health',
        icon: HiBanknotes,
        description: 'Cash flow, profitability, financial planning',
      },
      {
        key: 'sales',
        title: 'Sales & Marketing',
        icon: HiChartBar,
        description: 'Revenue growth, customer acquisition, market position',
      },
      {
        key: 'operations',
        title: 'Operations',
        icon: HiCog,
        description: 'Process efficiency, quality control, scalability',
      },
      {
        key: 'hr',
        title: 'Human Resources',
        icon: HiUserGroup,
        description: 'Team performance, culture, talent management',
      },
      {
        key: 'compliance',
        title: 'Compliance & Risk',
        icon: HiShieldCheck,
        description: 'Legal compliance, risk management, governance',
      },
    ],
    []
  );

  const overallScore = useMemo(
    () => getOverallDiagnosticScore(categories, checklistData || {}),
    [categories, checklistData]
  );

  const getStatusColor = status => {
    switch (status) {
      case 'yes':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'no':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'partial':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default:
        return 'text-gray-400 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = status => {
    switch (status) {
      case 'yes':
        return <HiCheckCircle className="h-5 w-5" />;
      case 'no':
        return <HiXCircle className="h-5 w-5" />;
      case 'partial':
        return <HiMinusCircle className="h-5 w-5" />;
      default:
        return <HiMinusCircle className="h-5 w-5" />;
    }
  };

  const getPriorityStyles = priority => {
    switch (priority) {
      case 'high':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'medium':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'low':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const toggleCategory = id => {
    setExpandedCategories(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleResponseChange = (categoryKey, item, response) => {
    if (!canEdit) return;
    onEditItem?.(categoryKey, item.id, {
      response,
      status: mapDiagnosticResponseToStatus(response),
    });
  };

  const handleNotesChange = (categoryKey, item, notes) => {
    if (!canEdit) return;
    onEditItem?.(categoryKey, item.id, { notes });
  };

  const handleFileChange = async (categoryKey, item) => {
    if (!canUpload) return;
    const file = fileInputs[item.id];
    if (!file) return;
    await onFileUpload(file, categoryKey, item.id);
    setFileInputs(inputs => ({ ...inputs, [item.id]: null }));
  };

  const handleAddItem = () => {
    if (!newItem.category || !newItem.question) {
      toast.error('Please fill in all required fields');
      return;
    }
    const item = {
      id:
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `item_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      question: newItem.question,
      priority: newItem.priority,
      response: '',
      status: 'not-started',
      notes: '',
      createdAt: new Date().toISOString(),
    };
    onAddItem?.(newItem.category, item);
    setNewItem({ category: '', question: '', priority: 'medium' });
    setShowAddForm(false);
  };

  const handleEditItemSave = () => {
    if (!editingItem?.categoryKey || editingItem?.id == null) return;
    onEditItem?.(editingItem.categoryKey, editingItem.id, {
      question: editingItem.question,
    });
    setEditingItem(null);
  };

  const handleDeleteClick = (categoryKey, itemId) => {
    if (!canDelete) return;
    setDeleteConfirm({ isOpen: true, categoryKey, itemId });
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirm.categoryKey || !deleteConfirm.itemId) return;
    setIsDeleting(true);
    try {
      onDeleteItem?.(deleteConfirm.categoryKey, deleteConfirm.itemId);
      setDeleteConfirm({ isOpen: false, categoryKey: null, itemId: null });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, categoryKey: null, itemId: null });
  };

  const overallLabel =
    overallScore >= 80
      ? 'Excellent'
      : overallScore >= 60
        ? 'Good'
        : overallScore >= 40
          ? 'Fair'
          : overallScore >= 20
            ? 'Poor'
            : 'Critical';

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6"
      >
        <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white">
              <HiClipboardDocumentCheck className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 md:text-2xl">Diagnostic Checklist</h2>
              <p className="text-gray-600">
                Structured diagnostic—same flow as the business health assessment: Yes, Partial, or
                No per item, with notes and evidence.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setShowNotes(!showNotes)}
              className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              {showNotes ? 'Hide Notes' : 'Show Notes'}
            </button>
            {canAdd && (
              <button
                type="button"
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                <HiPlus className="h-4 w-4" />
                Add item
              </button>
            )}
          </div>
        </div>

        <div className="rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 p-3 md:p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-gray-900 md:text-lg">
                Overall checklist score
              </h3>
              <p className="text-xs text-gray-600 md:text-sm">
                Based on answered items (Yes = full, Partial = half weight)
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600 md:text-3xl">{overallScore}%</div>
              <div className="text-xs text-gray-600 md:text-sm">{overallLabel}</div>
            </div>
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
          >
            <h3 className="mb-4 text-lg font-semibold text-gray-900">Add checklist item</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Category</label>
                <select
                  value={newItem.category}
                  onChange={e => setNewItem({ ...newItem, category: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">Select category</option>
                  {categories.map(category => (
                    <option key={category.key} value={category.key}>
                      {category.title}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Priority</label>
                <select
                  value={newItem.priority}
                  onChange={e => setNewItem({ ...newItem, priority: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">Question</label>
              <textarea
                value={newItem.question}
                onChange={e => setNewItem({ ...newItem, question: e.target.value })}
                placeholder="Enter the diagnostic question..."
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={handleAddItem}
                className="flex-1 rounded-lg bg-blue-600 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
              >
                Add item
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="flex-1 rounded-lg border border-gray-300 bg-white py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-4">
        {categories.map((category, categoryIndex) => {
          const items = checklistData[category.key] || [];
          const categoryScore = getCategoryDiagnosticScore(category.key, checklistData || {});
          const isExpanded = expandedCategories[category.key];
          const CategoryIcon = category.icon;
          const answeredCount = items.filter(i => {
            const r = getEffectiveDiagnosticResponse(i);
            return r === 'yes' || r === 'partial' || r === 'no';
          }).length;

          return (
            <motion.div
              key={category.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: categoryIndex * 0.05 }}
              className="rounded-xl border border-gray-200 bg-white shadow-sm"
            >
              <button
                type="button"
                onClick={() => toggleCategory(category.key)}
                className="w-full p-6 text-left hover:bg-gray-50"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="flex-shrink-0 text-blue-600">
                      <CategoryIcon className="h-6 w-6" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 md:text-lg">
                        {category.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {items.length} items • {answeredCount} answered
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-4">
                    <div className="text-right">
                      <div className="text-lg font-bold text-blue-600">{categoryScore}%</div>
                      <div className="text-xs text-gray-500">Score</div>
                    </div>
                    {isExpanded ? (
                      <HiChevronDown className="h-5 w-5 text-gray-400" />
                    ) : (
                      <HiChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </button>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border-t border-gray-100"
                  >
                    <div className="p-6">
                      {items.length === 0 ? (
                        <p className="text-center text-sm text-gray-500">
                          No items in this category yet.
                          {canAdd ? ' Use “Add item” above.' : ''}
                        </p>
                      ) : (
                        <>
                          <div className="mb-4 grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
                            <div className="col-span-12 md:col-span-6">Question</div>
                            <div className="col-span-12 text-center md:col-span-2 md:text-center">
                              Status
                            </div>
                            {showNotes && (
                              <div className="col-span-12 md:col-span-4">Notes / evidence</div>
                            )}
                          </div>

                          <div className="space-y-6">
                            {items.map(item => {
                              const effective = getEffectiveDiagnosticResponse(item);
                              return (
                                <div
                                  key={item.id}
                                  className="grid grid-cols-12 items-start gap-4 border-b border-gray-100 pb-6 last:border-0 last:pb-0"
                                >
                                  <div className="col-span-12 md:col-span-6">
                                    <div className="flex flex-wrap items-center gap-2">
                                      {String(editingItem?.id) === String(item.id) &&
                                      editingItem?.categoryKey === category.key ? (
                                        <input
                                          type="text"
                                          value={editingItem.question}
                                          onChange={e =>
                                            setEditingItem({
                                              ...editingItem,
                                              question: e.target.value,
                                            })
                                          }
                                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                          disabled={!canEdit}
                                          autoFocus
                                        />
                                      ) : (
                                        <p className="text-sm text-gray-900">{item.question}</p>
                                      )}
                                      <span
                                        className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${getPriorityStyles(item.priority)}`}
                                      >
                                        {item.priority}
                                      </span>
                                    </div>
                                    {(canEdit || item.attachment) && (
                                      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                                        {item.attachment && (
                                          <a
                                            href={item.attachment}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline"
                                          >
                                            <HiPaperClip className="h-4 w-4" />
                                            Attachment
                                          </a>
                                        )}
                                        {canUpload && (
                                          <>
                                            <input
                                              type="file"
                                              id={`file-${category.key}-${item.id}`}
                                              className="hidden"
                                              onChange={e =>
                                                setFileInputs(inputs => ({
                                                  ...inputs,
                                                  [item.id]: e.target.files[0],
                                                }))
                                              }
                                              disabled={uploading}
                                            />
                                            <label
                                              htmlFor={`file-${category.key}-${item.id}`}
                                              className="inline-flex cursor-pointer items-center gap-1 text-blue-600 hover:underline"
                                            >
                                              <HiArrowUpTray className="h-4 w-4" />
                                              {uploading ? 'Uploading…' : 'Upload'}
                                            </label>
                                            {fileInputs[item.id] && !uploading && (
                                              <button
                                                type="button"
                                                onClick={() => handleFileChange(category.key, item)}
                                                className="rounded-md bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700"
                                              >
                                                Save file
                                              </button>
                                            )}
                                          </>
                                        )}
                                        {canEdit && (
                                          <div className="ml-auto flex items-center gap-1 md:ml-0">
                                            {String(editingItem?.id) === String(item.id) &&
                                            editingItem?.categoryKey === category.key ? (
                                              <>
                                                <button
                                                  type="button"
                                                  onClick={handleEditItemSave}
                                                  className="rounded-lg p-2 text-emerald-600 hover:bg-emerald-50"
                                                  title="Save question"
                                                >
                                                  <HiCheckCircle className="h-5 w-5" />
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => setEditingItem(null)}
                                                  className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                                                  title="Cancel"
                                                >
                                                  <HiXCircle className="h-5 w-5" />
                                                </button>
                                              </>
                                            ) : (
                                              <>
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    setEditingItem({
                                                      id: item.id,
                                                      question: item.question,
                                                      categoryKey: category.key,
                                                    })
                                                  }
                                                  className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
                                                  title="Edit question"
                                                >
                                                  <HiPencilSquare className="h-4 w-4" />
                                                </button>
                                                {canDelete && (
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      handleDeleteClick(category.key, item.id)
                                                    }
                                                    className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                                                    title="Delete"
                                                  >
                                                    <HiTrash className="h-4 w-4" />
                                                  </button>
                                                )}
                                              </>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  <div className="col-span-12 md:col-span-2">
                                    {canEdit ? (
                                      <div className="flex flex-col gap-2">
                                        {['yes', 'partial', 'no'].map(status => (
                                          <button
                                            key={status}
                                            type="button"
                                            onClick={() =>
                                              handleResponseChange(category.key, item, status)
                                            }
                                            className={`flex items-center justify-center gap-2 rounded-lg border p-2 text-xs font-medium transition-all hover:scale-[1.02] ${
                                              effective === status
                                                ? getStatusColor(status)
                                                : 'border-gray-200 bg-white text-gray-400 hover:bg-gray-50'
                                            }`}
                                          >
                                            {getStatusIcon(status)}
                                            {status.charAt(0).toUpperCase() + status.slice(1)}
                                          </button>
                                        ))}
                                      </div>
                                    ) : (
                                      <div
                                        className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${getStatusColor(effective || '')}`}
                                      >
                                        {getStatusIcon(effective || '')}
                                        {effective
                                          ? effective.charAt(0).toUpperCase() + effective.slice(1)
                                          : 'Not answered'}
                                      </div>
                                    )}
                                  </div>

                                  {showNotes && (
                                    <div className="col-span-12 md:col-span-4">
                                      {canEdit ? (
                                        <textarea
                                          value={item.notes || ''}
                                          onChange={e =>
                                            handleNotesChange(category.key, item, e.target.value)
                                          }
                                          placeholder="Notes, actions, or observations…"
                                          rows={2}
                                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        />
                                      ) : (
                                        <div className="min-h-[40px] rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">
                                          {item.notes || 'No notes'}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
      >
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Checklist summary</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5">
          {categories.map(category => (
            <div key={category.key} className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {getCategoryDiagnosticScore(category.key, checklistData || {})}%
              </div>
              <div className="text-xs text-gray-600 md:text-sm">{category.title}</div>
            </div>
          ))}
        </div>
      </motion.div>

      <ConfirmationModal
        isOpen={deleteConfirm.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete checklist item"
        message="Are you sure you want to delete this item?"
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="bg-red-600 hover:bg-red-700"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default DiagnosticChecklist;
